import * as moment from 'moment';
import * as Sentry from '@sentry/angular';
import { environment } from '../../../../environments/environment';
import {
  mapPersonalDataToInsured,
  mapPersonalDataToOwner,
} from '../../shared/helpers/map-personal-data-to-idefend-contact';
import { CalculateOfferResponse } from '../../shared/interfaces/calculate-offer-response.interface';
import { CreationLockPayload } from '../../shared/interfaces/creation-lock-payload.interface';
import { PolicyCreatorStateModel } from '../store/policy-creator.model';
import { calculatePremiumForAnnual } from './calculate-premium-for-annual';
import { calculatePremiumForInstalments } from './calculate-premium-for-instalments';
import { checkForInstalments } from './check-for-instalments';
import { newIdefendConditionsMapper } from './idefend-mappers';

export function mapOfferLockPayload(
  policyCreatorState: PolicyCreatorStateModel,
  offer: CalculateOfferResponse,
): CreationLockPayload {
  try {
    const insurer = policyCreatorState.thirdStepPayload.insurer;
    const insuredForm = policyCreatorState.thirdStepPayload.insured;
    const isCesja = insuredForm.sameAsInsurer === false;
    const insuredParty = isCesja ? insuredForm : insurer;

    const ownerForm = policyCreatorState.fourthStepPayload.owner;
    const ownerParty =
      ownerForm?.sameAsInsurer === false ? ownerForm : insurer;

    const handlePremium = (): number => {
      switch (policyCreatorState.secondStepPayload.paymentType) {
        case 'PT_LS': {
          return offer.premiumDiscounted ?? offer.premiumSuggested;
        }
        case 'PT_LS_M': {
          return calculatePremiumForInstalments(
            offer.premiumDiscounted ?? offer.premiumSuggested,
            policyCreatorState.secondStepPayload.paymentPeriod,
          ).premiumCalculated;
        }
        case 'PT_A': {
          return calculatePremiumForAnnual(
            offer.premiumDiscounted ?? offer.premiumSuggested,
            policyCreatorState.secondStepPayload.policyPeriod,
          ).instalment;
        }
        case 'PT_LS_AIS': {
          return calculatePremiumForInstalments(
            offer.premiumDiscounted ?? offer.premiumSuggested,
            12,
          ).premiumCalculated;
        }
      }
    };

    const prepareNotes = (offerNotes): string => {
      let notes: string = ' ';
      if (offerNotes.discountLink) {
        notes = `Link Afiliacyjny: ${offerNotes.discountLink}\n`;
      }
      if (offerNotes.discountCode) {
        notes = `Kod Rabatowy: ${offerNotes.discountCode}`;
      }

      return notes;
    };

    const options = {
      ...offer.options,
    };

    let signatureTypeCode = 'AUTHORIZED_BY_PAYMENT';

    if (options.PAYMENT_METHOD === 'PM_BT') {
      signatureTypeCode = 'AUTHORIZED_BY_SMS';
    } else {
      signatureTypeCode = 'AUTHORIZED_BY_PAYMENT';
    }

    const policyHolder = mapPersonalDataToInsured(insurer, {
      includeContactDetails: true,
    });
    const insured = mapPersonalDataToInsured(insuredParty);
    const registryNo = policyCreatorState.fourthStepPayload.registryNo;

    const responseDto: CreationLockPayload = {
      endorsementTypeCode: offer.endorsementTypeCode,
      productCode: offer.productCode,
      saleInitiatedOn: moment(offer.saleInitiatedOn).format(),
      sellerNodeCode: environment.SELLER_NODE_CODE,
      instalmentSplittingSchemeCode: checkForInstalments(policyCreatorState),
      vehicleSnapshot: {
        modelCode: offer.vehicleSnapshot.modelCode,
        usageTypeCode: offer.vehicleSnapshot.usageTypeCode,
        usageCode: offer.vehicleSnapshot.usageCode,
        purchasePriceInputType: offer.vehicleSnapshot.purchasePriceInputType,
        purchasePrice: offer.vehicleSnapshot.purchasePrice,
        purchasePriceNet: offer.vehicleSnapshot.purchasePriceNet,
        purchasePriceVatReclaimableCode:
          offer.vehicleSnapshot.purchasePriceVatReclaimableCode,
        specialVehicle: offer.vehicleSnapshot.specialVehicle,
        categoryCode: policyCreatorState.firstStepPayload.vehicleType.code,
        vin: policyCreatorState.fourthStepPayload.vin,
        vrm: registryNo ? String(registryNo).toUpperCase() : undefined,
        firstRegisteredOn: moment(
          policyCreatorState.firstStepPayload.vehicleRegDate,
        ).format('YYYY-MM-DD'),
        purchasedOn: moment(
          policyCreatorState.firstStepPayload.vehicleBuyDate || new Date(),
        ).format('YYYY-MM-DD'),
        mileage: policyCreatorState.fourthStepPayload.millage,
        owners: [mapPersonalDataToOwner(ownerParty)],
      },
      options: options,
      signatureTypeCode: signatureTypeCode,
      premium: handlePremium(),
      inceptionDate: moment(offer.inceptionDate).format('YYYY-MM-DD'),
      client: {
        policyHolder,
        insured,
        ...(isCesja
          ? { beneficiary: mapPersonalDataToInsured(insuredParty) }
          : {}),
      },
      note: prepareNotes(offer),
    };

    newIdefendConditionsMapper(
      policyCreatorState.firstStepPayload,
      policyCreatorState.secondStepPayload,
      responseDto,
    );

    return responseDto;
  } catch (e) {
    console.error('offer lock payload error:', e);
    Sentry.captureException(e, {
      tags: {
        location: 'offerLockPayloadMapper',
        portfolioCode: policyCreatorState.portfolioSelected?.productCode,
      },
      extra: {
        state: policyCreatorState,
      },
    });
    return null;
  }
}
